import React, { useState, useRef, useEffect } from 'react';
import SidebarItem from './SidebarItem/SidebarItem';
import {
  ArrowLeftStartOnRectangleIcon,
  PuzzlePieceIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  UsersIcon,
  UserCircleIcon,
  ShieldCheckIcon,
  ShoppingBagIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../context/AuthContext';
import ThemeController from '../Common/ThemeController/ThemeController';
import { useNavigate } from 'react-router-dom';
import { ROLES } from '../../utils/roles.utils';
import { BlindsIcon, HelpCircleIcon, MessageCircleWarningIcon } from 'lucide-react';

const SidebarMenu = () => {
  const [isMinified, setIsMinified] = useState(true);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const sidebarRef = useRef(null);

  const handleMinified = () => {
    setIsMinified(!isMinified);
  };

  const handleClickOutside = (event) => {
    if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
      setIsMinified(true);
    }
  };

  const openHelpTab = () => {
    window.open('https://lumencare.notion.site/Aide-Verbatim-1e22ac8c5a478011b3d1d3f9efccd7db', '_blank');
  }

  const openProblemeTab = () => {
    window.open('https://lumencare.notion.site/Signalement-de-bug-ou-demande-d-am-lioration-1e32ac8c5a4780399e60c4d5e0f62d38', '_blank');
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <aside
      ref={sidebarRef}
      className={`
        fixed top-0 left-0 h-full
        ${isMinified ? 'w-[70px]' : 'w-72'}
        transition-all duration-300 ease-in-out
        bg-primary
        flex flex-col
        shadow-xl
        z-[999]
      `}
    >
      {/* Toggle Button */}
      <button
        onClick={handleMinified}
        className="absolute -right-3 top-6 bg-primary p-1.5 rounded-full shadow-lg hover:bg-primary-dark transition-colors duration-300"
        aria-label={isMinified ? "Expand sidebar" : "Collapse sidebar"}
      >
        {isMinified ? (
          <ChevronDoubleRightIcon className="w-4 h-4 text-white" />
        ) : (
          <ChevronDoubleLeftIcon className="w-4 h-4 text-white" />
        )}
      </button>

      {/* Header Section */}
      <div className="px-2 py-4 border-b border-white/10">
        <div className="flex items-center justify-center mb-4">
          <img
            src="/assets/logo_no_text.png"
            alt="logo"
            className={`${isMinified ? 'w-10 h-10' : 'w-16 h-16'} cursor-pointer transition-all duration-300`}
            onClick={() => navigate('/')}
          />
        </div>

        <div className="flex flex-col items-center gap-2">
          {user && (
            <div
              className={`${isMinified ? 'w-8 h-8' : 'w-fit h-fit py-1 px-2'} rounded-full bg-white/10 flex items-center justify-center`}
              title={`${user.firstname} ${user.lastname}`}
            >
              <p className="text-white font-medium text-sm">
                {isMinified ? user.firstname.charAt(0).toUpperCase() : `${user.firstname} ${user.lastname}`}
              </p>
            </div>
          )}
          {user && user?.currentCredits &&
            <div
              className={`flex items-center w-fit gap-2 bg-white/10 px-3 py-1 rounded-full ${isMinified ? 'w-full justify-center' : ''}`}
              title="Crédits restants"
            >
              <ShoppingBagIcon
                className="w-4 h-4 flex-shrink-0 text-white" />
              <span className="text-xs text-white font-medium">
                {user?.currentCredits}
              </span>
            </div>
          }
          {!isMinified && <ThemeController />}
        </div>
      </div>

      {/* Navigation Section */}
      <nav className="flex-1 overflow-y-visible py-4">
        <div className="space-y-1 px-2">
          <SidebarItem to="/clients" label="Clients" isMinified={isMinified}>
            <UsersIcon className="w-5 h-5" />
          </SidebarItem>
          {/* <SidebarItem to="/mes-capsules" label="Mes Capsules" isMinified={isMinified}>
            <BeakerIcon className="w-5 h-5" />
          </SidebarItem> */}
          <SidebarItem to="/melanges/personnalises" label="Mélanges Personnalisés" isMinified={isMinified}>
            <PuzzlePieceIcon className="w-5 h-5" />
          </SidebarItem>
          <SidebarItem to="/melanges/catalogue" label="Catalogue" isMinified={isMinified}>
            <BlindsIcon className="w-5 h-5" />
          </SidebarItem>

          <SidebarItem to="/produits" label="Commander des encodages" isMinified={isMinified}>
            <ShoppingBagIcon className="w-5 h-5" />
          </SidebarItem>

        </div>
      </nav>

      {/* Footer Section */}
      <div className="border-t border-white/10 p-2">
        <SidebarItem to="/mon-compte" label="Mon Compte" isMinified={isMinified}>
          <UserCircleIcon className="w-5 h-5" />
        </SidebarItem>
        <SidebarItem onClick={openHelpTab} label="Aide / Infos" isMinified={isMinified}>
          <HelpCircleIcon className="w-5 h-5" />
        </SidebarItem>
        <SidebarItem onClick={openProblemeTab} label="Signaler un problème" isMinified={isMinified}>
          <MessageCircleWarningIcon className="w-5 h-5" />
        </SidebarItem>

      </div>
      <div className="border-t border-white/10 p-2">

        {user?.permission.value === ROLES.ADMIN.value && (
          <SidebarItem to="/admin" label="Administration" isMinified={isMinified}>
            <ShieldCheckIcon className="w-5 h-5" />
          </SidebarItem>
        )}

        <SidebarItem onClick={logout} label="Déconnexion" isMinified={isMinified}>
          <ArrowLeftStartOnRectangleIcon className="w-5 h-5" />
        </SidebarItem>
      </div>
    </aside>
  );
};

export default SidebarMenu;
